import React, {FC, memo, useState} from "react";
import {Space} from "antd";
import moment from "moment";
import {v4 as uuidv4} from "uuid";
import css from "./PanelCardPayments.less";
import PanelCard from "../PanelCard/PanelCard";
import i18n from "../../services/i18n";
import OpenPaymentsList from "../../containers/payments/OpenPaymentsList";
import DonePaymentsList from "../../containers/payments/DonePaymentsList";
import {GetBookingByIdQuery, GetBookingByIdWithCourseLessonBookingsQuery, Maybe, ScheduledPayment, ScheduledPaymentType, TablesEnum} from "../../generated/graphql";
import {margin, marginXs} from "../../styles/layout";
import GenerateSchedulePaymentsButton from "./GenerateSchedulePaymentsButton";
import {RenderAmount} from "../TableColumnRenderers/TableColumnRenderers";
import AddNamedScheduledPayment from "./AddNamedScheduledPayment";
import SettledBookingMemberDiscount from "./SettledBookingMemberDiscount";
import SettledBookingDiscount from "./SettledBookingDiscount";
import SettledBookingCredit from "./SettledBookingCredit";
import ResetPaymentPlanButton from "../../containers/invoice/SettlementDetails/ResetPaymentPlanButton";
import {calcPaymentTotals} from "./paymentTotals";
import useHighestRole from "../../helpers/useHighestRole";
import {useLiveRefresh} from "../../helpers/useLiveRefresh";
import Roles from "../../../shared/models/roles";

type PossibleBookingType = GetBookingByIdQuery["bookingById"] | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type PanelCardPaymentsProps = {
    bookingId?: string;
    // booking?: GetBookingByIdQuery["bookingById"];
    booking?: PossibleBookingType;
    // Nur die Felder, die die Karte liest: die Abrechnungsdetails reichen Raten aus einer anderen Abfrage durch.
    scheduledPayments?: Maybe<Array<Pick<ScheduledPayment, "type" | "paymentDone" | "openAmount" | "paymentAmount">>>;
    timeStamp: number;
    setTimeStamp?: (timeStamp: number) => void;
    isCanceled: boolean;
    refetchBooking?: () => void;
};

// const pollInterval = 6000;
const pollInterval = undefined;

const PanelCardPayments: FC<PanelCardPaymentsProps> = ({bookingId, booking, scheduledPayments, timeStamp, isCanceled, refetchBooking, setTimeStamp}) => {
    // const [newTimeStamp, setNewTimeStamp] = useState(moment().unix());

    // #684: „Posten neu generieren" ist serverseitig auf Superuser beschränkt
    // (scheduledPayment.resolver.ts). Ohne diese Prüfung sähen andere Rollen einen
    // Knopf, der nur noch mit 403 antwortet.
    const highestRole = useHighestRole();
    // Nach DSGVO-Löschantrag lehnt der Server Änderungen am Zahlungsplan, Rabatt und Guthaben ab;
    // Zahlungseingang und Löschen einer Rate bleiben (Listen unten).
    const attendeeErased = Boolean(booking?.attendee?.erasedAt);

    React.useEffect(() => {
        refetchBooking?.();
    }, [refetchBooking, timeStamp]);

    // Live-Aktualisierung: Buchung (Summen, Preisübersicht) und beide Listen laden nach. Pilot WM 14.10.:
    // bezahlte Rate verschwand aus „Offen“, fehlte aber in „Bezahlt“. Fremde Änderungen laufen über
    // `liveStamp`, damit die Listen geänderte Raten markieren; eigene lädt der Dialog selbst nach.
    // 30 s: ein Nachladen kostet hier drei Abfragen (Buchung, offene und bezahlte Raten).
    const [liveStamp, setLiveStamp] = useState<number>();
    useLiveRefresh(
        async () => {
            refetchBooking?.();
            setLiveStamp(Date.now());
        },
        false,
        30000,
    );

    // Berechne ob alle Standard-Posten bezahlt sind
    const allPaid = (() => {
        const standardPayments = (scheduledPayments ?? []).filter((p) => p.type === ScheduledPaymentType.Standard);

        return standardPayments.length > 0 && standardPayments.every((p) => p.paymentDone !== null);
    })();

    // Offene Posten nutzen openAmount ?? paymentAmount (reduzierter Rest nach Teilzahlung/Verrechnung),
    // bezahlte Posten zählen mit vollem paymentAmount. Details siehe paymentTotals.ts.
    const {openAmount, doneAmount, sumAmount} = calcPaymentTotals(scheduledPayments ?? []);

    const SumAmount = () => {
        return (
            <div>
                <div className={css.sumAmount}>
                    <div className={css.sumAmountText}>{i18n.containers.invoice.InvoiceDetails.sumAmount()}</div>
                    <div className={css.sumAmountValue}>
                        <RenderAmount amount={sumAmount ?? 0} style={{fontSize: "unset", fontWeight: "unset"}} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <PanelCard
            columns={1}
            title={i18n.containers.invoice.InvoiceDetails.payments()}
            gapAfter
            extra={
                <Space>
                    {!scheduledPayments?.length &&
                        booking?.canceled === null &&
                        !attendeeErased &&
                        highestRole === Roles.Superuser &&
                        bookingId && <GenerateSchedulePaymentsButton bookingId={bookingId} />}
                    <SumAmount />
                </Space>
            }
        >
            {!isCanceled && bookingId && (
                <>
                    <OpenPaymentsList
                        tablesEnum={TablesEnum.SettledBookingOpenPaymentsList}
                        hideTitle
                        mandatoryColumns={["paymentDue", "title"]}
                        customColumns={["paymentDue", "title", "paymentMethod", "paymentAmount", "tombstoneStatus", "setPaidAction"]}
                        // hidePagination
                        hideXmlBtn
                        style={{
                            marginBottom: marginXs,
                            marginTop: "30px",
                            paddingLeft: 0,
                            paddingRight: 0,
                            marginLeft: -10,
                            marginRight: -10,
                            width: "calc(100% + 10px)",
                        }}
                        bookingId={bookingId}
                        hiddenFilters={["courseNumber"]}
                        minPadding
                        pollInterval={pollInterval}
                        tableTitle={
                            <Space>
                                {"Offene Posten"}
                                <RenderAmount
                                    amount={openAmount ?? 0}
                                    style={{
                                        fontSize: "unset",
                                        fontWeight: "unset",
                                    }}
                                />
                            </Space>
                        }
                        hideSpecialFunctions
                        hideFooter
                        hideExportButton
                        timeStamp={timeStamp}
                        liveStamp={liveStamp}
                        setTimeStamp={setTimeStamp}
                    />
                    {!attendeeErased && (
                        <>
                            <div style={{display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "space-between", alignItems: "center", marginTop: 8, marginBottom: marginXs}}>
                                <div style={{display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end", marginLeft: "auto"}}>
                                    <SettledBookingMemberDiscount booking={booking} bookingId={bookingId} allPaid={allPaid} setTimeStamp={setTimeStamp} />
                                    <SettledBookingDiscount booking={booking} bookingId={bookingId} allPaid={allPaid} setTimeStamp={setTimeStamp} />
                                    <SettledBookingCredit booking={booking} bookingId={bookingId} allPaid={allPaid} setTimeStamp={setTimeStamp} />
                                </div>
                            </div>
                            <AddNamedScheduledPayment bookingId={bookingId} monthlySchedule={booking?.monthlySchedule} setTimeStamp={setTimeStamp} />
                            <ResetPaymentPlanButton booking={booking} refetch={() => setTimeStamp?.(moment.unix(Date.now()).valueOf())} />
                        </>
                    )}
                </>
            )}
            <DonePaymentsList
                tablesEnum={TablesEnum.SettledBookingDonePaymentsList}
                hideTitle
                customColumns={["paymentDone", "title", "paymentMethod", "paymentAmount", "tombstoneStatus", "setUnPaidAction"]}
                // hidePagination
                hideXmlBtn
                style={{
                    marginBottom: marginXs,
                    marginTop: margin,
                    paddingLeft: 0,
                    paddingRight: 0,
                    marginLeft: -10,
                    marginRight: -10,
                    width: "calc(100% + 10px)",
                }}
                bookingId={bookingId}
                hiddenFilters={["courseNumber"]}
                minPadding
                pollInterval={pollInterval}
                tableTitle={
                    <Space>
                        {"Bezahlte Posten"} <RenderAmount amount={doneAmount ?? 0} style={{fontSize: "unset", fontWeight: "unset"}} />
                    </Space>
                }
                hideSpecialFunctions
                hideFooter
                hideExportButton
                timeStamp={timeStamp}
                liveStamp={liveStamp}
                setTimeStamp={setTimeStamp}
            />
        </PanelCard>
    );
};

export default memo(PanelCardPayments);
